import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { FieldText, FieldTextarea, FieldSelect } from "@/pages/Clients";
import {
  ArrowCounterClockwiseIcon,
  DownloadSimpleIcon,
  UserCircleIcon,
  BuildingsIcon,
  QrCodeIcon,
  ShieldCheckIcon,
  UploadSimpleIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

const PIX_TYPES = [
  { value: "email", label: "E-mail" },
  { value: "cpf", label: "CPF" },
  { value: "cnpj", label: "CNPJ" },
  { value: "phone", label: "Telefone" },
  { value: "random", label: "Chave aleatória" },
];

export default function Settings() {
  const { user } = useAuth();
  const { settings, loaded, save } = useSettings();
  const [form, setForm] = useState(settings);
  const [hydrated, setHydrated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  // Hydrate the form ONCE when settings is loaded from the server.
  // After that, keep the form as the user's editable buffer.
  useEffect(() => {
    if (loaded && !hydrated) {
      setForm(settings);
      setHydrated(true);
    }
  }, [loaded, hydrated, settings]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm({ ...form, logo_data: await fileToBase64(file) });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (user?.role !== "admin") {
      toast.error("Apenas admin pode salvar configurações");
      return;
    }
    setSaving(true);
    try {
      const saved = await save(form);
      setForm({ ...form, ...saved });
      toast.success("Configurações salvas com sucesso");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const isDirty =
    hydrated &&
    JSON.stringify(form) !== JSON.stringify({ ...settings, ...{} });

  const resetSamples = async () => {
    if (!window.confirm("Isso apagará todos os dados e recriará os de demonstração. Continuar?")) return;
    setBusy(true);
    try {
      await api.post("/seed/reset");
      toast.success("Dados de exemplo recriados");
    } catch {
      toast.error("Erro ao redefinir dados");
    } finally {
      setBusy(false);
    }
  };

  const exportBackup = async () => {
    setBusy(true);
    try {
      const [c, o, e, p, i, s] = await Promise.all([
        api.get("/clients"),
        api.get("/orders"),
        api.get("/expenses"),
        api.get("/parts"),
        api.get("/invoices"),
        api.get("/settings"),
      ]);
      const data = {
        exported_at: new Date().toISOString(),
        clients: c.data,
        orders: o.data,
        expenses: e.data,
        parts: p.data,
        invoices: i.data,
        settings: s.data,
      };
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `assistencia_backup_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup baixado");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader title="Configurações" subtitle="Empresa, conta, PIX, garantia e backup." />

      <form onSubmit={submit} className="space-y-6 mb-6">
        {/* Empresa */}
        <div className="glass rounded-xl p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-10 w-10 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400">
              <BuildingsIcon size={22} weight="duotone" />
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold">Dados da empresa</h3>
              <p className="text-xs text-slate-500">Aparecem no PDF da Ordem de Serviço.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FieldText label="Nome / Razão social *" value={form.company_name} onChange={set("company_name")} required testid="settings-company-name" />
            <FieldText label="CNPJ" value={form.cnpj} onChange={set("cnpj")} testid="settings-cnpj" />
            <FieldText label="Telefone" value={form.phone} onChange={set("phone")} testid="settings-phone" />
            <FieldText label="E-mail" value={form.email} onChange={set("email")} testid="settings-email" />
          </div>
          <div className="mt-4">
            <FieldText label="Endereço" value={form.address} onChange={set("address")} testid="settings-address" />
          </div>

          <div className="mt-4">
            <span className="block text-xs uppercase tracking-wider text-slate-400 mb-1.5">Logo</span>
            <div className="flex items-center gap-4">
              <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 cursor-pointer text-sm transition border border-white/10">
                <UploadSimpleIcon size={16} /> Enviar logo
                <input type="file" accept="image/*" onChange={handleLogo} className="hidden" data-testid="settings-logo-input" />
              </label>
              {form.logo_data && (
                <div className="flex items-center gap-3">
                  <img src={form.logo_data} alt="logo" className="h-12 w-auto bg-white/10 rounded p-1" />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, logo_data: "" })}
                    className="text-xs text-rose-400 hover:text-rose-300"
                  >
                    Remover
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* PIX */}
        <div className="glass rounded-xl p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-10 w-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
              <QrCodeIcon size={22} weight="duotone" />
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold">Pagamento via PIX</h3>
              <p className="text-xs text-slate-500">Gera QR Code automático no PDF da OS para receber direto.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FieldSelect label="Tipo de chave" value={form.pix_key_type} onChange={set("pix_key_type")} options={PIX_TYPES} testid="settings-pix-type" />
            <FieldText label="Chave PIX" value={form.pix_key} onChange={set("pix_key")} testid="settings-pix-key" placeholder="ex: contato@empresa.com" />
            <FieldText label="Cidade (sem acento, máx 15)" value={form.pix_city} onChange={set("pix_city")} testid="settings-pix-city" />
          </div>
          <div className="mt-4">
            <FieldText label="Nome do recebedor (no QR Code)" value={form.pix_merchant_name} onChange={set("pix_merchant_name")} testid="settings-pix-merchant" placeholder="Padrão: nome da empresa" />
          </div>
        </div>

        {/* Garantia & Termos */}
        <div className="glass rounded-xl p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-10 w-10 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400">
              <ShieldCheckIcon size={22} weight="duotone" />
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold">Garantia e termo de entrega</h3>
              <p className="text-xs text-slate-500">Texto padrão exibido no rodapé do PDF da OS.</p>
            </div>
          </div>
          <FieldTextarea label="Termos de garantia" value={form.warranty_text} onChange={set("warranty_text")} rows={3} testid="settings-warranty" />
          <div className="mt-4">
            <FieldTextarea label="Termo de entrega" value={form.delivery_terms} onChange={set("delivery_terms")} rows={3} testid="settings-delivery-terms" />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          {isDirty && (
            <span className="text-xs text-amber-400 inline-flex items-center gap-1.5" data-testid="settings-dirty">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" />
              Alterações não salvas
            </span>
          )}
          <button
            type="submit"
            disabled={saving || user?.role !== "admin"}
            data-testid="settings-save-btn"
            className="bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-medium px-5 py-2.5 rounded-lg shadow-[0_0_15px_rgba(37,99,235,0.4)] transition hover:-translate-y-0.5"
          >
            {saving ? "Salvando..." : "Salvar configurações"}
          </button>
        </div>
      </form>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass rounded-xl p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-12 w-12 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400">
              <UserCircleIcon size={24} weight="duotone" />
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold">Minha conta</h3>
              <p className="text-xs text-slate-500">Informações do usuário logado</p>
            </div>
          </div>
          <dl className="space-y-3">
            <div className="flex justify-between text-sm border-b border-white/5 pb-2">
              <dt className="text-slate-400">Nome</dt>
              <dd className="font-medium">{user?.name}</dd>
            </div>
            <div className="flex justify-between text-sm border-b border-white/5 pb-2">
              <dt className="text-slate-400">E-mail</dt>
              <dd className="font-medium">{user?.email}</dd>
            </div>
            <div className="flex justify-between text-sm">
              <dt className="text-slate-400">Função</dt>
              <dd>
                <span className="inline-block px-2 py-0.5 rounded-md text-xs bg-blue-500/10 text-blue-300 border border-blue-500/20 capitalize">
                  {user?.role}
                </span>
              </dd>
            </div>
          </dl>
        </div>

        <div className="glass rounded-xl p-6">
          <h3 className="font-display text-lg font-semibold mb-1">Backup & dados</h3>
          <p className="text-xs text-slate-500 mb-5">Exporte seus dados ou restaure os dados de demonstração.</p>
          <div className="space-y-3">
            <button
              onClick={exportBackup}
              disabled={busy}
              data-testid="backup-export"
              className="w-full bg-emerald-600/15 hover:bg-emerald-600/25 text-emerald-300 border border-emerald-500/20 px-4 py-3 rounded-lg flex items-center gap-2 transition disabled:opacity-50"
            >
              <DownloadSimpleIcon size={18} weight="bold" /> Exportar backup (JSON)
            </button>
            <button
              onClick={resetSamples}
              disabled={busy || user?.role !== "admin"}
              data-testid="reset-samples"
              className="w-full bg-rose-600/15 hover:bg-rose-600/25 text-rose-300 border border-rose-500/20 px-4 py-3 rounded-lg flex items-center gap-2 transition disabled:opacity-50"
            >
              <ArrowCounterClockwiseIcon size={18} weight="bold" /> Redefinir dados de exemplo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
